/**
 * PaymentAuthorizationManager
 * Selects the correct payment authorization message from the settings and
 * delivers the result to the consumer event hook and to the
 * payment-authorization fragment via the command dispatcher.
 *
 * This class is not exposed to the consuming application.
 */

import { createLogger } from '@anddone/ajs-logger';
import type {
  PaymentMethod,
  TransactionSettingsPaymentAuthorizationStatement,
  PaymentAuthorizationProcessMethod,
  PaymentAuthorizationPasType,
  CommandDispatcher,
} from '@anddone/ajs-util';
import type { Events } from '../Events';
import type { SettingsState } from './SettingsState';

const logger = createLogger('SDK', 'PaymentAuthorizationManager');

export class PaymentAuthorizationManager {
  // Most-recently selected authorization message; null until first published.
  #current: string | null = null;

  constructor(
    private readonly settings: SettingsState,
    private readonly dispatcher: CommandDispatcher,
    private readonly events: Events,
  ) {}

  /**
   * Most-recently selected authorization message, or null until first published.
   */
  public get current(): string | null {
    return this.#current;
  }

  /**
   * Select the authorization message for the given payment method, emit
   * `payment-authorization-change`, and send `update-payment-authorization`
   * to the payment-authorization fragment.
   *
   * Returns early (with a debug log) when settings have not been loaded yet.
   * The dispatcher skips the command when the payment-authorization fragment
   * is not registered; the consumer event hook fires regardless.
   *
   * @param paymentMethod - The active payment method
   */
  public publish(paymentMethod: PaymentMethod): void {
    const settings = this.settings.current;
    if (!settings) {
      logger.debug('publish', 'Skipped — settings not yet available.');
      return;
    }

    const authorizationMessage = this.selectMessage(
      paymentMethod,
      settings.paymentAuthorizationStatement,
    );

    this.#current = authorizationMessage;

    if (!authorizationMessage) {
      logger.warn('publish', 'No matching authorization statement found for payment method.', {
        paymentMethod,
      });
    } else {
      logger.info('publish', 'Message Selected.', {
        paymentMethod,
        authorizationMessage,
      });
    }

    this.events.emit('payment-authorization-change', {
      authorizationMessage,
    });

    // Dispatch to all fragment types that manage the authorization message internally.
    // The standalone payment-authorization display fragment plus the full-checkout fragments
    // that embed the auth message alongside their payment fields.
    // Dispatch returns false (with a debug log) for any target not currently registered,
    // so it is safe to list all potential recipients here.
    const authCommandPayload = {
      authorizationMessage,
      // commandToken is stamped by OrchestratorCommandManager.createCommandMessage before postMessage.
      commandToken: '',
    };
    this.dispatcher.dispatch(
      'payment-authorization',
      'update-payment-authorization',
      authCommandPayload,
    );
    this.dispatcher.dispatch(
      'full-card-checkout',
      'update-payment-authorization',
      authCommandPayload,
    );
    this.dispatcher.dispatch(
      'full-ach-checkout',
      'update-payment-authorization',
      authCommandPayload,
    );
    this.dispatcher.dispatch(
      'full-multi-channel-checkout',
      'update-payment-authorization',
      authCommandPayload,
    );
  }

  private selectMessage(
    paymentMethod: PaymentMethod,
    paymentAuthorizationMessages: readonly TransactionSettingsPaymentAuthorizationStatement[],
  ): string {
    const processMethod: PaymentAuthorizationProcessMethod =
      paymentMethod === 'ach' ? 'ACH' : 'CardNotPresent';
    // For now, we only support pay in full pasType. Once financing is added,
    // choose between that and 'InstallmentWithDownPayment'
    const pasType: PaymentAuthorizationPasType = 'PayInFull';
    for (const message of paymentAuthorizationMessages) {
      if (pasType === message.pasType && processMethod === message.processMethod) {
        return message.body;
      }
    }

    return '';
  }
}

Object.freeze(PaymentAuthorizationManager.prototype);
